// src/components/layout/Footer.tsx
import React, { useState } from 'react';
import { X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import packageJson from '../../../package.json';
import releasesData from '../../data/releases.json';

interface ReleaseNotes {
  version: string;
  date: string;
  notes: string;
  url: string;
}

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const appVersion = packageJson.version;
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<string>(`v${appVersion}`);

  // Get build date (set during build by Vercel or use current date in dev)
  const buildDate = import.meta.env.VITE_BUILD_DATE || new Date().toISOString().split('T')[0];

  // Load releases from bundled JSON file
  const allReleases: ReleaseNotes[] = releasesData.map(release => ({
    version: release.version,
    date: new Date(release.publishedAt).toLocaleDateString(),
    notes: release.notes,
    url: ''
  }));

  const handleVersionClick = () => {
    setShowReleaseNotes(true);
    setSelectedVersion(`v${appVersion}`); // Default to current version
  };

  // Get the currently selected release
  const selectedRelease = allReleases.find(release => release.version === selectedVersion) || null;

  return (
    <>
      <footer className="bg-white border-t border-ink-200 mt-auto">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-ink-500">
            {/* Left side - Version info */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleVersionClick}
                className="font-medium text-ink-700 hover:text-navy-600 transition-colors cursor-pointer underline decoration-dotted"
                title="Click to view what's new"
              >
                PIT v{appVersion}
              </button>
              <span className="hidden sm:inline text-ink-400">|</span>
              <span className="text-xs text-ink-400">
                Updated {buildDate}
              </span>
            </div>

          {/* Right - Copyright */}
          <div className="flex items-center gap-1 text-ink-400">
            <span>© {currentYear} PIT — Preferences, Intentions &amp; Timetabling</span>
          </div>
        </div>
      </div>
    </footer>

    {/* Release Notes Modal */}
    {showReleaseNotes && (
      <div className="fixed inset-0 bg-navy-950/45 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-e3 max-w-2xl w-full max-h-[80vh] overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-ink-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-ink-900">
                Release Notes
              </h2>
              <button
                onClick={() => setShowReleaseNotes(false)}
                className="text-ink-400 hover:text-ink-500 transition-colors"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Version Selector */}
            <div className="flex items-center gap-3">
              <label htmlFor="version-select" className="text-sm font-medium text-ink-700 whitespace-nowrap">
                Select Version:
              </label>
              <select
                id="version-select"
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(e.target.value)}
                className="flex-1 px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-600/25 focus:border-navy-600 text-sm"
              >
                {allReleases.map((release) => (
                  <option key={release.version} value={release.version}>
                    {release.version} - {release.date}
                  </option>
                ))}
              </select>
            </div>

            {selectedRelease && (
              <p className="text-sm text-ink-500 mt-3">
                Released on {selectedRelease.date}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {selectedRelease ? (
              <div className="prose prose-sm max-w-none text-ink-700">
                <ReactMarkdown
                  components={{
                    h2: ({ node, ...props }) => <h2 className="text-lg font-bold mt-4 mb-2 text-ink-900" {...props} />,
                    h3: ({ node, ...props }) => <h3 className="text-base font-semibold mt-3 mb-2 text-ink-900" {...props} />,
                    ul: ({ node, ...props }) => <ul className="list-disc ml-4 space-y-1" {...props} />,
                    li: ({ node, ...props }) => <li className="text-sm" {...props} />,
                    p: ({ node, ...props }) => <p className="mb-2 text-sm" {...props} />,
                    strong: ({ node, ...props }) => <strong className="font-semibold text-ink-900" {...props} />,
                    a: ({ node, ...props }) => <a className="text-navy-600 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
                    hr: ({ node, ...props }) => <hr className="my-4 border-ink-300" {...props} />,
                  }}
                >
                  {selectedRelease.notes}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="text-center py-8 text-ink-500">
                No release notes available for this version.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 border-t border-ink-200 bg-ink-50">
            <button
              onClick={() => setShowReleaseNotes(false)}
              className="px-4 py-2 bg-navy-900 text-white rounded-md hover:bg-navy-800 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Footer;
